import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, timer, from } from 'rxjs';
import { catchError, mergeMap, retry } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Constants } from '../../constants';
import { AuthService } from '../Services/auth.service';

interface RetryRequestOptions {
  maximumRetries: number;
  retryDelay: number;
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  private retryRequestOptions: RetryRequestOptions = {
    maximumRetries: 3,
    retryDelay: 200, // milliseconds
  };

  constructor(
    private authService: AuthService,
    private router: Router
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          // Handle 401 Unauthorized separately
          return this.handle401Error();
        }
        return throwError(() => err);
      }),
      this.applyRetryLogic(request) // Apply retry logic conditionally
    );
  }

  private handle401Error(): Observable<never> {
    return from(this.authService.logout()).pipe(
      mergeMap(() => this.router.navigate([Constants.loginPage])),
      mergeMap(() => throwError(() => new Error('Unauthorized')))
    );
  }

  private applyRetryLogic<T>(request: HttpRequest<any>) {
    // Apply retry only for URLs containing '/api/analysisrequests'
    if (request.url.includes(Constants.analysisRequestsEndpoint)) {
      return this.retryPipe<T>();
    }
    // Otherwise, return the stream without retry
    return (source: Observable<T>) => source;
  }

  private retryPipe<T>() {
    const { maximumRetries, retryDelay } = this.retryRequestOptions;

    return retry<T>({
      count: maximumRetries,
      delay: (error: HttpErrorResponse, retryCount: number) => {
        // Retry only for 5xx server errors, 408 and network errors (status 0)
        if ((error.status >= 500 && error.status < 600) || error.status === 0 || error.status === 408) {
          const delayDuration = retryDelay * Math.pow(2, retryCount); // Exponential backoff
          return timer(delayDuration);
        }
        return throwError(() => error); // Stop retrying for other errors
      }
    });
  }
}
